import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BellRing, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POLL_MS = 15000;

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume();
    [880, 1174.66, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.22;
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.55);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch {}
}

export default function PaymentAlert() {
  const [count, setCount] = useState(0);
  const [ringing, setRinging] = useState(false);
  const lastLatest = useRef(null);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/payments/pending");
        if (!active) return;
        setCount(data.count || 0);
        const latest = data.latest || "";
        if (lastLatest.current === null) {
          lastLatest.current = latest;
        } else if (latest && latest > lastLatest.current) {
          lastLatest.current = latest;
          setRinging(true);
          playChime();
          toast.warning("🔔 Nouveau paiement à vérifier !", {
            description: "Un client vient de déclarer un paiement. Vérifiez la transaction.",
            duration: 10000,
          });
        } else if (!data.count) {
          setRinging(false);
        }
      } catch {}
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (!count) return null;

  return (
    <div
      data-testid="payment-alert-banner"
      className={`mb-4 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-orange-300 bg-orange-50 p-3.5 pl-4 ${
        ringing ? "alert-blink" : ""
      }`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white ${ringing ? "animate-bounce" : ""}`}>
        <BellRing className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-orange-800">
          🟠 {count} paiement{count > 1 ? "s" : ""} à vérifier
        </p>
        <p className="text-xs text-orange-700">
          {ringing ? "Un nouveau paiement vient d'arriver — vérifiez la transaction." : "Des clients attendent la vérification de leur paiement."}
        </p>
      </div>
      <Link
        to="/admin/commandes"
        data-testid="payment-alert-link"
        onClick={() => setRinging(false)}
        className="flex items-center gap-1.5 rounded-full bg-orange-500 px-4 py-2.5 text-xs font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
      >
        Vérifier maintenant <ArrowRight className="h-3.5 w-3.5" />
      </Link>
      {ringing && (
        <button
          data-testid="payment-alert-dismiss"
          onClick={() => setRinging(false)}
          title="Arrêter le clignotement"
          className="rounded-lg p-1.5 text-orange-400 hover:bg-orange-100 hover:text-orange-700"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
